import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import BackgroundImageCst from "@components/BackgroundImageCst";
import { Box, Container, Typography } from "@mui/material";
import { useFetchTable } from "@hooks/useFetchDataTable";
import { useFetchDataDetail } from "@hooks/useFetchDetail";
import Table from "@components/Table";
import HeroCst from "@components/HeroCst";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import ModalDownloadUploadCst from "@components/ModalDownloadUploadCst";
import { ActionUploadDownloadIterface } from "@components/ModalDownloadUploadCst/types";
import { downloadFile, uploadFile } from "@utils/utilsApi";
import { ActionsType } from "src/interfaces/Common";
import { KeyURL, MethodType } from "src/services/type";
import { fetcher } from "src/services/config";
import useSWRMutation from "swr/mutation";
import AnimationFadeIn from "@components/AnimationFadeIn";
import CustomRowAction from "@components/Table/components/CustomRowAction";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@context/auth";
import { useFilters } from "@context/filters";
import ButtonBackPage from "@components/ButtonBackPage";
import HeroProject from "../HeroProject";
import Breadcrumb from "@components/Breadcrumb";
import EditNoteIcon from "@mui/icons-material/EditNote";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";

const DetailCost: React.FC = () => {
  const [idCostDetailDeleted, setIdCostDetailDeleted] = useState<string>();
  const [idCostRequested, setIdCostRequested] = useState<string>();
  const [openUpload, setOpenUpload] = useState<ActionUploadDownloadIterface>();
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [openModalRequest, setOpenModalRequest] = useState(false);
  const { idProject, id, idCostModel } = useParams();
  const { user } = useAuth();
  const { filters } = useFilters();
  const filterId = `${user?.id_user}_${PagesID["projects.cost-detail"]}`;
  const { t } = useTranslation(["projects-cost-detail"]);
  const navigate = useNavigate();
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
  }>();

  /*#######################################*/

  /*Permission to indetify PM*/
  const isPM = user?.permissions.find(
    (permission: any) =>
      permission === "usermgmt.budget.project.mark.as.pending.activation",
  );

  /*#######################################*/

  /*Get the project information*/
  const { dataDetail: dataProject, mutate: projectMutate } = useFetchDataDetail(
    `${ServicesURL.getProjects}/${idProject}?corporateId=${id}`,
    PagesID["projects.cost-model"],
  );

  const { columns, dataTable, actionsTopToolbar, actionForRow, mutate } =
    useFetchTable(
      `${ServicesURL.getCostModelDetail}?costModelItemId=${idCostModel}&corporateId=${id}`,
      PagesID["projects.cost-detail"],
      filters[filterId],
    );

  /*Formatting the data from server*/
  const dataFormat = useMemo(() => {
    if (!dataTable?.output) return [];

    return dataTable.output.map((el: any) => {
      if (el.supplier) {
        el.supplier.label = el.supplier?.business_name || "-";
      }

      if (el.contract) {
        el.contract.label = el.contract?.sap_code || "-";
      }

      if (el.extra_data) {
        el.cost_model_item_account =
          el.extra_data?.cost_model_item_account || "-";
      }

      if (el.extra_data) {
        el.project_manager = el.extra_data?.project_manager || "-";
      }

      return el;
    });
  }, [dataTable?.output]);

  /*Trigger to delete a detail cost*/
  const { trigger: triggerDeleteCostDetail } = useSWRMutation(
    `${ServicesURL.postCostModelDetail}/${idCostDetailDeleted}?corporateId=${id}`,
    (url: string) => {
      return fetcher<any>(url, MethodType.DELETE);
    },
  );

  /*Trigger to mark as requestes a detail cost*/
  const { trigger: triggerRequestCostDetail } = useSWRMutation(
    `${ServicesURL.postCostModelDetail}/${idCostRequested}/actions/mark-as-requested?corporateId=${id}`,
    (url: string) => {
      return fetcher<any>(url, MethodType.POST);
    },
  );

  /*Function to handle action mark as requested*/
  const handleMarkAsRequested = React.useCallback(async () => {
    if (idCostRequested) {
      setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
      try {
        await triggerRequestCostDetail();
        mutate && mutate({ ...dataTable });
        setOpenToast({ s: StatusEnum.OK, message: "" });
      } catch (error) {
        setIdCostRequested(undefined);
        setOpenToast(undefined);
        console.error(error);
      }
    }
  }, [dataTable, idCostRequested, mutate, triggerRequestCostDetail]);

  /*Function to handle the delete acition*/
  const handleDelete = React.useCallback(async () => {
    if (idCostDetailDeleted) {
      setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
      try {
        await triggerDeleteCostDetail();
        const newData = [
          ...dataTable.output.filter(
            (el: any) => el.id !== idCostDetailDeleted,
          ),
        ];
        mutate && mutate({ ...dataTable, ...{ output: newData } });
        setOpenToast({ s: StatusEnum.OK, message: "" });
      } catch (error) {
        setIdCostDetailDeleted(undefined);
        setOpenToast(undefined);
        console.error(error);
      }
    }
  }, [dataTable, idCostDetailDeleted, mutate, triggerDeleteCostDetail]);

  useEffect(() => {
    if (!idCostDetailDeleted) return;
  }, [idCostDetailDeleted]);

  const filteredColumns = useMemo(() => {
    if (columns) {
      if (isPM) {
        return columns?.filter(
          (item: any) => item.accessorKey !== "project_manager",
        );
      } else {
        return columns;
      }
    }
  }, [columns]);

  const configurationActionForRow: ActionsType[] = React.useMemo(() => {
    return actionForRow?.map((action: any) => {
      switch (action.type) {
        case "delete": {
          return {
            ...action,
            callBack: (id: string) => {
              setIdCostDetailDeleted(id);
              setOpenModalDelete(true);
            },
          };
        }
        case "button": {
          switch (action.key) {
            case "request-cost":
              return {
                ...action,
                getIcon: () => <EditNoteIcon />,
                callBack: (data: any) => {
                  setIdCostRequested(data?.id || null);
                  setOpenModalRequest(true);
                },
              };
            case "goods-receipt":
              return {
                ...action,
                getIcon: () => <ShoppingCartIcon />,
                callBack: (row: any) => {
                  navigate(
                    `/corporate/${id}/detail/budget-management/projects/edit/${idProject}/cost-model/${idCostModel}/cost/${row.id}/goods-receipts`,
                  );
                },
              };
            default:
              return action;
          }
        }
        default:
          return action;
      }
    });
  }, [actionForRow, navigate]);

  const customRenderRowActions = ({ row }: { row: any }): React.ReactNode => {
    let configRowNew: any = configurationActionForRow && [
      ...configurationActionForRow,
    ];

    configRowNew = configRowNew.map((el: ActionsType) => {
      /*Disable delete if not deletable*/
      if (el.type === "delete" && !row.original?.deletable) {
        return { ...el, disabled: true };
      }

      if (el.type === "delete" && isPM && row.original?.status !== "DRAFT") {
        return { ...el, disabled: true };
      }

      /*Button request-cost only if status is DRAFT and management_type is defined*/
      if (
        el.type === "button" &&
        el.key == "request-cost" &&
        (row.original.status != "DRAFT" ||
          row.original.management_type === "TBD")
      ) {
        return {};
      }

      /*Button goods-receipt only if status is ISSUED*/
      if (
        el.type === "button" &&
        el.key == "goods-receipt" &&
        row.original.status == "ISSUED"
      ) {
        return { ...el, disabled: false };
      }

      return { ...el };
    });

    return [
      <CustomRowAction
        key={row.id}
        row={row}
        configurationActionForRow={configRowNew}
      />,
    ];
  };

  const actionsToolbar: ActionsType[] | undefined = React.useMemo(() => {
    return actionsTopToolbar?.map((action: ActionsType) => {
      switch (action.type) {
        case "add":
          return {
            ...action,
            disabled: !!(isPM && dataProject?.dto?.status === "CLOSED"),
          };
        case "upload": {
          const actionModal: ActionUploadDownloadIterface = {
            ...action,
            ...{
              service: `${ServicesURL[action.service as KeyURL]}?corporate_id=1`,
              types: ["upload", "download", "template"],
              onDownload: (el) =>
                downloadFile(
                  `${ServicesURL[action.service as KeyURL]}/download${
                    el ? `/${el}` : ""
                  }?corporate_id=1&type=LEARNER` || "",
                ),
              onUpload: (formData: FormData) => {
                return uploadFile(
                  `${ServicesURL[action.service as KeyURL]}?corporate_id=1`,
                  formData,
                );
              },
            },
          };
          return {
            ...action,
            ...{ callBack: () => setOpenUpload(actionModal) },
          };
        }
        case "visibility": {
          const actionModal: ActionUploadDownloadIterface = {
            ...action,
            ...{
              service: `${
                ServicesURL[action.service as KeyURL]
              }?corporate_id=1`,
              types: ["upload", "download", "template"],
              onDownload: (el) =>
                downloadFile(
                  `${ServicesURL[action.service as KeyURL]}/?corporate_id=1` ||
                    "",
                ),
              onUpload: (formData: FormData) =>
                uploadFile(
                  `${ServicesURL[action.service as KeyURL]}?corporate_id=1`,
                  formData,
                ),
            },
          };
          return {
            ...action,
            ...{ callBack: () => setOpenUpload(actionModal) },
          };
        }
        default:
          return action;
      }
    });
  }, [actionsTopToolbar]);

  return (
    <AnimationFadeIn>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-project.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth="xxl"
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroProject />

        <ButtonBackPage
          customNavigate={() =>
            navigate(
              `/corporate/${id}/detail/budget-management/projects/edit/${idProject}`,
              { state: { tab: "cost-model" } },
            )
          }
        />
        <HeroCst title={t("hero-title")} description={""} />
        <Table
          id={PagesID["projects.cost-detail"]}
          data={dataFormat || []}
          columns={filteredColumns || []}
          configurationTopToolbar={actionsToolbar}
          numberOfActions={configurationActionForRow?.length}
          renderRowActions={customRenderRowActions}
          totalPages={dataTable?.metadata?.total_elements}
          modalTitle={t("hero-title")?.replace("-", "") || ""}
        />
        <ModalConfirmsCst
          open={!!openModalDelete}
          title={t("delete-item", { ns: "modal" })}
          description={t("confirm-delete", { ns: "modal" })}
          routeToSamePage={true}
          onCallBackClosed={() => {
            setOpenModalDelete(!openModalDelete);
            setOpenToast(undefined);
          }}
          onActionConfirmed={() => handleDelete()}
          status={openToast}
        />
        <ModalConfirmsCst
          open={!!openModalRequest}
          title={t("mark-request")}
          description={t("confirm-mark-request")}
          routeToSamePage={true}
          onCallBackClosed={() => {
            setOpenModalRequest(!openModalRequest);
            setOpenToast(undefined);
          }}
          onActionConfirmed={() => handleMarkAsRequested()}
          status={openToast}
        />
        <ModalDownloadUploadCst
          open={!!openUpload}
          setOpen={() => {
            setOpenUpload(undefined);
          }}
          title={openUpload?.labelTranslate || ""}
          data={openUpload}
        />
      </Container>
    </AnimationFadeIn>
  );
};

export default DetailCost;
